<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";

import { formatPhone, isValidPhone } from "@/helpers/phone";
import { CandidateFieldType, isValidCandidateField } from "@/modules/worker/helpers/candidateFormValidation";
import { useWorkerAddIdentificationStore } from "@/modules/worker/store/workerAddIdentificationStore";

import TextInputAlt from "@/dsComponents/inputs/TextInputAlt.vue";

import CandidateOriginsDropdown from "@/components/dropdown/CandidateOriginsDropdown.vue";
import JobsDropdownMultiple from "@/components/dropdown/JobsDropdownMultiple.vue";

const { t } = useI18n();

const workerAddIdentificationStore = useWorkerAddIdentificationStore();

const birthDateInputType = ref("text");

const handlePhoneBlur = () => {
    if (isValidPhone(workerAddIdentificationStore.findWorkerForm.phone.value)) {
        formatPhone(workerAddIdentificationStore.findWorkerForm.phone.value);
    }
};
</script>

<template>
    <div class="create-candidate-form">
        <div class="create-candidate-form__item">
            <div class="create-candidate-form__item-label typo-xxs-medium">
                {{ t("form.lastname") }}
                <span class="create-candidate-form__item-label--required">*</span>
            </div>

            <TextInputAlt
                v-model="workerAddIdentificationStore.findWorkerForm.lastname.value"
                class="create-candidate-form__item-input"
                variant="grey"
                icon-left="doc-name"
                name="lastname"
                :placeholder="t('page.worker.modal.create.form.placeholder.lastname')"
                :error="!workerAddIdentificationStore.findWorkerForm.lastname.isValid"
                :state="workerAddIdentificationStore.findWorkerForm.lastname.value ? 'filled' : 'empty'"
                @update:model-value="workerAddIdentificationStore.findWorkerForm.lastname.isValid = isValidCandidateField($event, CandidateFieldType.String)"
            />
        </div>

        <div class="create-candidate-form__item">
            <div class="create-candidate-form__item-label typo-xxs-medium">
                {{ t("form.firstname") }}
                <span class="create-candidate-form__item-label--required">*</span>
            </div>

            <TextInputAlt
                v-model="workerAddIdentificationStore.findWorkerForm.firstname.value"
                class="create-candidate-form__item-input"
                variant="grey"
                icon-left="doc-name"
                name="firstname"
                :placeholder="t('page.worker.modal.create.form.placeholder.firstname')"
                :error="!workerAddIdentificationStore.findWorkerForm.firstname.isValid"
                :state="workerAddIdentificationStore.findWorkerForm.firstname.value ? 'filled' : 'empty'"
                @update:model-value="workerAddIdentificationStore.findWorkerForm.firstname.isValid = isValidCandidateField($event, CandidateFieldType.String)"
            />
        </div>

        <div class="create-candidate-form__item">
            <div class="create-candidate-form__item-label typo-xxs-medium">
                {{ t("page.worker.modal.create.form.label.birthday") }}
            </div>

            <TextInputAlt
                v-model="workerAddIdentificationStore.findWorkerForm.birthDate.value"
                class="create-candidate-form__item-input"
                variant="grey"
                icon-left="cal-dot"
                name="birthDate"
                :type="birthDateInputType"
                :placeholder="t('page.worker.modal.create.form.placeholder.birthday')"
                :error="!workerAddIdentificationStore.findWorkerForm.birthDate.isValid"
                :state="workerAddIdentificationStore.findWorkerForm.birthDate.value ? 'filled' : 'empty'"
                @update:model-value="workerAddIdentificationStore.findWorkerForm.birthDate.isValid = isValidCandidateField($event, CandidateFieldType.Date)"
                @focus="birthDateInputType = 'date'"
            />
        </div>

        <div class="create-candidate-form__item">
            <div class="create-candidate-form__item-label typo-xxs-medium">
                {{ t("form.phone") }}
                <span class="create-candidate-form__item-label--required">*</span>
            </div>

            <TextInputAlt
                v-model="workerAddIdentificationStore.findWorkerForm.phone.value"
                class="create-candidate-form__item-input"
                variant="grey"
                icon-left="phone"
                name="phone"
                type="tel"
                :placeholder="t('page.worker.modal.create.form.placeholder.phone')"
                :state="workerAddIdentificationStore.findWorkerForm.phone.value ? 'filled' : 'empty'"
                :error="!workerAddIdentificationStore.findWorkerForm.phone.isValid"
                @update:model-value="workerAddIdentificationStore.findWorkerForm.phone.isValid = isValidCandidateField($event, CandidateFieldType.Phone)"
                @blur="handlePhoneBlur"
            />
        </div>

        <div class="create-candidate-form__item">
            <div class="create-candidate-form__item-label typo-xxs-medium">
                {{ t("form.email") }}
                <span class="create-candidate-form__item-label--required">*</span>
            </div>

            <TextInputAlt
                v-model="workerAddIdentificationStore.findWorkerForm.email.value"
                class="create-candidate-form__item-input"
                variant="grey"
                icon-left="at"
                name="email"
                :placeholder="t('page.worker.modal.create.form.placeholder.email')"
                :state="workerAddIdentificationStore.findWorkerForm.email.value ? 'filled' : 'empty'"
                :error="!workerAddIdentificationStore.findWorkerForm.email.isValid"
                @update:model-value="workerAddIdentificationStore.findWorkerForm.email.isValid = isValidCandidateField($event, CandidateFieldType.Email)"
            />
        </div>

        <div class="create-candidate-form__item">
            <div class="create-candidate-form__item-label typo-xxs-medium">
                {{ t("page.worker.modal.create.form.label.origin") }}
                <span class="create-candidate-form__item-label--required">*</span>
            </div>

            <CandidateOriginsDropdown
                v-model="workerAddIdentificationStore.findWorkerForm.origin.value"
                class="create-candidate-form__item-input"
                full-width
            />
        </div>

        <div class="create-candidate-form__item">
            <div class="create-candidate-form__item-label typo-xxs-medium">
                {{ t("form.jobs") }}
                <span class="create-candidate-form__item-label--required">*</span>
            </div>

            <JobsDropdownMultiple
                class="create-candidate-form__item-input"
                :selected-jobs="workerAddIdentificationStore.findWorkerForm.jobs.value"
                :placeholder="t('page.worker.modal.create.form.placeholder.job')"
                :fit-content="false"
                full-width
                @update:selected-jobs="
                    (value) => {
                        workerAddIdentificationStore.findWorkerForm.jobs.value = value;
                    }
                "
            />
        </div>
    </div>
</template>

<style scoped lang="scss">
.create-candidate-form {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-md, 8px);
    width: 100%;
    padding-top: var(--spacing-md, 8px);

    &__item {
        display: flex;
        align-items: center;
        min-height: 28px;
        width: 100%;

        &-label {
            display: flex;
            align-items: center;
            gap: var(--spacing-xs, 4px);
            flex-shrink: 0;
            width: 120px;
            color: var(--text-sub-600, #494e61);

            &--required {
                color: var(--blue-base, #2051f8);
            }
        }

        &-input {
            flex: 1;
            min-width: 0;
        }
    }
}
</style>
